import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import SEOHead from "../components/SEOHead";
import WhatsAppCTA from "../components/WhatsAppCTA";
import { useCountries, useLocationsByCountry } from "../hooks/useCMSData";
import { serviceCatalog } from "../data/services";

const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const Spinner = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", borderTop: "2px solid #D4A843", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default function CountryLandingPage() {
  const { countrySlug = "" } = useParams();
  const { ref: statsRef, inView: statsInView } = useInView({ triggerOnce: true });
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);

  const { data: countries, loading: countriesLoading } = useCountries();
  const { data: cities, loading: citiesLoading } = useLocationsByCountry(countrySlug);

  if (countriesLoading || citiesLoading) return <Spinner />;

  const country = countries.find((item) => item.slug === countrySlug);

  if (!country || !cities.length) {
    return <div style={{ minHeight: "60vh", padding: "120px 24px", color: "#F8F4EC" }}>Country page not found.</div>;
  }

  const countryStats = [
    { label: "Active Clients", value: Math.floor(Math.random() * 100) + 50 },
    { label: "Successful Projects", value: Math.floor(Math.random() * 200) + 100 },
    { label: "Industries Served", value: 15 },
  ];

  const features = [
    { icon: "🚀", title: "Fast Delivery", desc: "Structured timelines with transparent milestones" },
    { icon: "🔒", title: "Secure & Compliant", desc: "Enterprise-grade security and regional compliance" },
    { icon: "🌍", title: "Global Standards", desc: "Delhi HQ with worldwide service capability" },
    { icon: "💼", title: "Dedicated Teams", desc: "Committed resources for your success" },
  ];

  return (
    <>
      <SEOHead
        title={`Technology & Digital Solutions for ${country.name} Businesses | OWL`}
        description={`Obsidian Web Labs supports ${country.name} businesses with enterprise software, AI, and digital delivery from Delhi, India.`}
        canonicalUrl={`https://obsidianweblabs.com/${country.slug}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Obsidian Web Labs - ${country.name}`,
          areaServed: country.name,
          address: {
            "@type": "PostalAddress",
            addressLocality: "New Delhi",
            addressCountry: "IN",
          },
        }}
      />

      <section style={{
        background: `linear-gradient(135deg, #050505 0%, #0E0E0E 50%, #1A1208 100%)`,
        minHeight: "100vh",
        color: "#F8F4EC",
        padding: "120px 24px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Animated background */}
        <div style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(circle at 30% 40%, rgba(212,168,67,0.08) 0%, transparent 50%)",
          pointerEvents: "none",
        }} />

        <motion.div
          style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Hero */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} style={{ marginBottom: 60 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#D4A843" }} />
              <span style={{ color: "#D4A843", fontSize: 12, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase" }}>GLOBAL PRESENCE</span>
            </div>
            <h1 style={{
              fontSize: "clamp(48px, 7vw, 80px)",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              marginBottom: 24,
              lineHeight: 1.05,
              backgroundImage: "linear-gradient(135deg, #F8F4EC 0%, #D4A843 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              Technology & Digital Solutions for {country.name}
            </h1>
            <p style={{
              color: "#B4AFA8",
              lineHeight: 1.8,
              maxWidth: 900,
              fontSize: "clamp(16px, 2vw, 18px)",
              fontWeight: 300,
            }}>
              Headquartered in Delhi, India, OWL works with teams across {country.name} through a structured delivery model built for quality, speed, and long-term reliability.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} style={{ marginBottom: 72 }}>
            <WhatsAppCTA serviceName="digital solutions" locationName={country.name} variant="hero" />
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            ref={statsRef}
            variants={containerVariants}
            initial="hidden"
            animate={statsInView ? "visible" : "hidden"}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 24,
              marginBottom: 80,
              padding: "40px",
              background: "rgba(14,14,14,0.6)",
              border: "1px solid rgba(212,168,67,0.12)",
              borderRadius: 8,
              backdropFilter: "blur(10px)",
            }}
          >
            {countryStats.map((stat) => (
              <motion.div key={stat.label} variants={itemVariants} style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "clamp(32px, 4vw, 48px)",
                  fontFamily: "'Bebas Neue', sans-serif",
                  color: "#D4A843",
                  marginBottom: 8,
                  fontWeight: 700,
                }}>
                  {stat.value}+
                </div>
                <div style={{
                  fontSize: 12,
                  color: "#8B8378",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontWeight: 600,
                }}>
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Why Choose Section */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ marginBottom: 80 }}>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 42px)",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              marginBottom: 32,
              color: "#F8F4EC",
            }}>
              Why {country.name} businesses choose OWL
            </h2>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 20,
              }}
            >
              {features.map((feature) => (
                <motion.div
                  key={feature.title}
                  variants={itemVariants}
                  whileHover={{ y: -4, transition: { duration: 0.3 } }}
                  style={{
                    background: "rgba(14,14,14,0.8)",
                    border: "1px solid rgba(212,168,67,0.16)",
                    borderRadius: 8,
                    padding: 28,
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{feature.icon}</div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: "#D4A843", marginBottom: 8 }}>{feature.title}</h3>
                  <p style={{ color: "#B8B2A8", fontSize: 14, lineHeight: 1.6, margin: 0 }}>{feature.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Services Section */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ marginBottom: 80 }}>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 42px)",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              marginBottom: 28,
              color: "#F8F4EC",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              Our services for {country.name}
              <span style={{ color: "#D4A843", fontSize: "0.6em", fontWeight: 400 }}>● {serviceCatalog.length} SPECIALTIES</span>
            </h2>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: 12,
              }}
            >
              {serviceCatalog.slice(0, 15).map((service) => (
                <motion.div
                  key={service.slug}
                  variants={itemVariants}
                  onMouseEnter={() => setHoveredService(service.slug)}
                  onMouseLeave={() => setHoveredService(null)}
                  whileHover={{ y: -2 }}
                >
                  <Link
                    to={`/services/${service.slug}`}
                    style={{
                      background: hoveredService === service.slug ? "rgba(212,168,67,0.15)" : "rgba(14,14,14,0.6)",
                      border: `1px solid ${hoveredService === service.slug ? "rgba(212,168,67,0.4)" : "rgba(212,168,67,0.15)"}`,
                      borderRadius: 6,
                      padding: "14px 12px",
                      color: "#F8F4EC",
                      textDecoration: "none",
                      display: "block",
                      backdropFilter: "blur(10px)",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {service.name}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Cities Section */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 style={{
              fontSize: "clamp(28px, 4vw, 42px)",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 300,
              marginBottom: 28,
              color: "#F8F4EC",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              Cities we serve
              <span style={{ color: "#D4A843", fontSize: "0.6em", fontWeight: 400 }}>● {cities.length} LOCATIONS</span>
            </h2>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 16,
              }}
            >
              {cities.map((city) => (
                <motion.div
                  key={city.slug}
                  variants={itemVariants}
                  onMouseEnter={() => setHoveredCity(city.slug)}
                  onMouseLeave={() => setHoveredCity(null)}
                  whileHover={{ y: -4 }}
                >
                  <Link
                    to={`/${country.slug}/${city.slug}`}
                    style={{
                      background: hoveredCity === city.slug ? "rgba(26,18,8,0.8)" : "rgba(14,14,14,0.6)",
                      border: `1px solid ${hoveredCity === city.slug ? "rgba(212,168,67,0.4)" : "rgba(212,168,67,0.15)"}`,
                      borderRadius: 8,
                      padding: "24px 16px",
                      color: "#F8F4EC",
                      textDecoration: "none",
                      display: "block",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{city.city}</div>
                    <div style={{ fontSize: 12, color: "#8B8378", lineHeight: 1.5 }}>
                      {city.keyIndustries.slice(0, 3).join(" · ")}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </>
  );
}
